import { getCurrentInstance, onUnmounted } from 'vue';
import axios from 'src/api/mutator/axios';

// Track active requests by entity type and params
const activeRequests = new Map<string, Promise<any>>();
// Store entity data by entity type
const entityStore = new Map<string, any[]>();
// Track subscribers per entity type
const subscribers = new Map<string, Set<string>>();

/**
 * Interface for request parameters
 */
interface RequestParams {
  page: number;
  limit: number;
  [key: string]: any;
}

/**
 * Creates a unique key for tracking requests based on entity and params
 */
function getRequestKey(entity: string, params: RequestParams, urlParams: Record<string, string | number> = {}): string {
  const urlParamsString = Object.entries(urlParams)
    .map(([key, value]) => `${key}:${value}`)
    .join('|');

  // Include all params in the key to ensure proper cache differentiation
  const paramsString = Object.entries(params)
    .sort(([a], [b]) => a.localeCompare(b)) // Sort for consistent keys
    .map(([key, value]) => `${key}:${value}`)
    .join('|');

  return `${entity}|${urlParamsString}|${paramsString}`;
}

/**
 * Hook for managing entity data fetching with request deduplication
 */
export function useEntityProvider() {
  // Generate a unique ID for this component instance
  const instanceId = getCurrentInstance()?.uid?.toString() || Math.random().toString(36).substring(2, 9);

  // Keep track of subscriptions for cleanup
  const subscribedEntities = new Set<string>();

  /**
   * Load entities with request deduplication
   */
  async function loadEntities<T>(
    entity: string,
    params: RequestParams = { page: 1, limit: 100 },
    urlParams: Record<string, string | number> = {},
    customFetch?: () => Promise<{ data: T[], total: number }>
  ) {
    const requestKey = getRequestKey(entity, params, urlParams);

    // Check if we already have an active request for this entity and params
    if (activeRequests.has(requestKey)) {
      return activeRequests.get(requestKey);
    }

    // Subscribe this component to updates for this entity
    if (!subscribers.has(entity)) {
      subscribers.set(entity, new Set());
    }
    subscribers.get(entity)?.add(instanceId);
    subscribedEntities.add(entity);

    // Create and store the request promise
    const fetchPromise = customFetch ?
      customFetch() :
      fetchEntityData(entity, params, urlParams);

    activeRequests.set(requestKey, fetchPromise);

    try {
      const result = await fetchPromise;

      // Store the result in our entity store
      if (result?.data) {
        entityStore.set(entity, result.data);
      }

      return result;
    } finally {
      // Clean up the active request reference
      activeRequests.delete(requestKey);
    }
  }

  /**
   * Standard entity API fetch
   */
  async function fetchEntityData(
    entity: string,
    params: RequestParams,
    urlParams: Record<string, string | number> = {}
  ) {
    let url = `/api/${entity}`;

    // Replace URL parameters
    Object.entries(urlParams).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });

    // Separate pagination params from search/filter params
    const { page, limit, search, searchFields, ...filterParams } = params;
    const queryParams = { page, limit };

    // Build request body with search criteria
    const body: Record<string, any> = { ...filterParams };

    // If search term is provided, add it to the appropriate fields
    if (search && searchFields && searchFields.length > 0) {
      // Check if search term is numeric
      const isNumeric = !isNaN(parseFloat(search)) && isFinite(Number(search));

      // Common numeric field patterns (fields that expect numbers)
      const numericFieldPatterns = ['price', 'cost', 'amount', 'quantity', 'margin', 'measurement'];

      // Fields that should be excluded from search (enums, booleans, etc.)
      const excludedFieldPatterns = ['status', 'active', 'main_', 'is_', 'has_', 'type'];

      // Filter fields based on whether they're likely numeric or excluded
      const fieldsToSearch = searchFields.filter((field: string) => {
        const fieldLower = String(field).toLowerCase();

        // Exclude enum/boolean fields
        const isExcludedField = excludedFieldPatterns.some(pattern => fieldLower.includes(pattern));
        if (isExcludedField) return false;

        const isNumericField = numericFieldPatterns.some(pattern => fieldLower.includes(pattern));

        // Include field if:
        // - It's not a numeric field (search string fields)
        // - OR it's a numeric field AND the search term is numeric
        return !isNumericField || isNumeric;
      });

      // Only add search fields if we have valid fields to search
      if (fieldsToSearch.length > 0) {
        // Add search term to filtered fields
        fieldsToSearch.forEach((field: string) => {
          const fieldLower = String(field).toLowerCase();
          const isNumericField = numericFieldPatterns.some(pattern => fieldLower.includes(pattern));

          // Convert to appropriate type
          body[field] = isNumericField && isNumeric ? parseFloat(search) : search;
        });

        // Use OR logic when searching multiple fields
        if (fieldsToSearch.length > 1) {
          body.match_mode = 'or';
        }
      }
    }

    const response = await axios.post(url, body, { params: queryParams });

    if (!response.data?.data) {
      return { data: [], total: 0 };
    }

    // Calculate total records from total_pages if total is not provided
    const total = response.data.total || (response.data.total_pages * response.data.limit);

    return {
      data: response.data.data,
      total
    };
  }

  /**
   * Get current entities for a specific entity type
   */
  function getEntities<T>(entity: string): T[] {
    return (entityStore.get(entity) || []) as T[];
  }

  /**
   * Check if entities are currently being loaded
   */
  function isLoading(entity: string, params: RequestParams = { page: 1, limit: 100 }, urlParams = {}): boolean {
    const requestKey = getRequestKey(entity, params, urlParams);
    return activeRequests.has(requestKey);
  }

  // Clean up subscriptions when component is unmounted
  onUnmounted(() => {
    subscribedEntities.forEach(entity => {
      const entitySubscribers = subscribers.get(entity);
      if (entitySubscribers) {
        entitySubscribers.delete(instanceId);
        if (entitySubscribers.size === 0) {
          subscribers.delete(entity);
        }
      }
    });
  });

  return {
    loadEntities,
    getEntities,
    isLoading
  };
}

// Export a single instance for global use
export const entityProvider = useEntityProvider();
